import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useState } from "react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import ArticleIcon from "@mui/icons-material/Article";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardCommandKeyIcon from "@mui/icons-material/KeyboardCommandKey";
import LogoutIcon from "@mui/icons-material/Logout";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";

import { useAnchoring } from "hooks";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { QUICK_LAUNCHER_FEATURE } from "constants/feature-flags";
import {
  getQuickLauncherHotkeyLabel,
  openQuickLauncherHelp
} from "features/app-layout/quick-launcher";
import { useSchedulerChoice } from "features/app-layout/scheduler-choice/use-scheduler-choice.hook";
import { useSwitchScheduler } from "features/app-layout/scheduler-choice/use-switch-scheduler.hook";
import {
  logoutUser,
  selectCurrentUser,
  selectCurrentUserToken
} from "features/auth/auth-slice";
import { clearAppDataAndReload } from "utils/clear-app-data.util";
import IntercomSingleton from "utils/intercom.util";
import UserbackSingleton from "utils/userback.util";

import ReferralsModal from "../referrals/referrals-modal.component";

export default function AccountMenu() {
  const currentUser = useAppSelector(selectCurrentUser);
  const intercom = IntercomSingleton.getInstance();
  const userback = UserbackSingleton.getInstance();
  const token = useAppSelector(selectCurrentUserToken);
  const dispatch = useAppDispatch();
  const flags = useFlags();
  // The guide describes a hotkey that only exists while the launcher does.
  const isQuickLauncherEnabled = Boolean(flags[QUICK_LAUNCHER_FEATURE]);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const closeModal = useCallback(() => {
    setModalIsOpen(false);
  }, []);

  const openModal = useCallback(() => {
    setModalIsOpen(true);
  }, []);

  const logout = useCallback(() => {
    if (token && dispatch) {
      dispatch(logoutUser({ token }));
      intercom.logout();
      userback.reset();
    }
  }, [token, dispatch, intercom, userback]);
  const navigate = useNavigate();
  const { anchorEl, handleClick, handleClose, open } = useAnchoring();

  return (
    <>
      <Stack
        data-testid="user-account-info"
        direction="column"
        alignItems="center"
        justifyContent="flex-start"
        sx={{
          boxSizing: "border-box",
          maxWidth: 120,
          height: 72
        }}
        spacing={0.5}
      >
        <IconButton
          onClick={handleClick}
          sx={{ my: currentUser.fullName.length > 20 ? -0.85 : -0.5 }}
          data-testid="account-button"
        >
          <Avatar
            variant="circular"
            alt={currentUser.fullName}
            src={currentUser.photo ?? ""}
          />
        </IconButton>
        <Typography
          variant="body1"
          component="div"
          onClick={handleClick}
          sx={{
            "&:hover": {
              opacity: 0.85
            },
            transform: "translateY(-8px)",
            cursor: "pointer",
            fontSize: "12px",
            lineHeight: 1,
            maxWidth: 120,
            overflow: "hidden",
            textOverflow: "ellipsis",
            textAlign: "center"
          }}
        >
          {currentUser.fullName}
        </Typography>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        sx={{
          zIndex: (theme) => theme.zIndex.snackbar
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0
              }
            }
          }
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => navigate("/settings/personal")}>
          <Avatar alt={currentUser.fullName} src={currentUser.photo ?? ""} /> My
          account
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => navigate("/settings")}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <NewSchedulerMenuItem onDone={handleClose} />
        <MenuItem onClick={openModal}>
          <Stack width={36}>
            <Typography fontSize={16}>🤝</Typography>
          </Stack>
          Earn $500
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => window.open("https://help.suno.tech/en/", "_blank")}
        >
          <ListItemIcon>
            <HelpOutlineIcon fontSize="small" />
          </ListItemIcon>
          Help Center
        </MenuItem>
        <MenuItem
          onClick={() =>
            window.open(
              "https://help.suno.tech/en/articles/14821982-release-notes",
              "_blank"
            )
          }
        >
          <ListItemIcon>
            <ArticleIcon fontSize="small" />
          </ListItemIcon>
          Release Notes
        </MenuItem>
        {isQuickLauncherEnabled && (
          <MenuItem
            onClick={() => dispatch(openQuickLauncherHelp())}
            data-testid="account-menu-shortcuts"
          >
            <ListItemIcon>
              <KeyboardCommandKeyIcon fontSize="small" />
            </ListItemIcon>
            Keyboard shortcuts
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: "auto", pl: 2 }}
            >
              {getQuickLauncherHotkeyLabel()}
            </Typography>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => setResetDialogOpen(true)}>
          <ListItemIcon>
            <RestartAltIcon fontSize="small" />
          </ListItemIcon>
          Reset App Data
        </MenuItem>
        <MenuItem onClick={logout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <ReferralsModal isOpen={modalIsOpen} onClose={closeModal} />

      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset App Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will clear locally stored data in this browser, including saved
            filters, table preferences, and offline caches. Your account,
            personal settings, and any data saved to Suno are not affected. You
            will stay logged in. This can help resolve display issues or
            unexpected behavior.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={clearAppDataAndReload} variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * ENG-1981: the profile menu's "New scheduler" switch — the permanent way in for anyone who
 * pressed "Not now" on the banner, and a second way back out. Offered only where the flag
 * offers the new scheduler, and only once the person's choice is known.
 */
export function NewSchedulerMenuItem({ onDone }: { onDone: () => void }) {
  const { offered, status, useV3 } = useSchedulerChoice();
  const { optIn, optOut } = useSwitchScheduler();
  if (!offered || status !== "settled") return null;

  const toggle = () => {
    onDone();
    if (useV3) {
      optOut("account_menu");
    } else {
      void optIn("account_menu");
    }
  };

  return (
    <MenuItem onClick={toggle}>
      <ListItemIcon>
        <EventAvailableOutlinedIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>New scheduler</ListItemText>
      <Switch
        edge="end"
        size="small"
        checked={useV3}
        inputProps={{ role: "switch", "aria-label": "New scheduler" }}
        tabIndex={-1}
        sx={{ ml: 2, pointerEvents: "none" }}
      />
    </MenuItem>
  );
}
